import ActiveIdentityLabel from '../layout/ActiveIdentityLabel';
import React, { useEffect, useMemo, useCallback, useState, useLayoutEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './MyProfileMenu.css';

/**
 * Contextual profile menu with avatar upload.
 * Props:
 * - user: { username, full_name, email, role }
 * - position: { x, y }
 * - avatarUrl: string | null
 * - onClose: () => void
 * - onAction: (action: string) => void
 * - onUploadAvatar: (file: File) => Promise<string> // should return URL
 */
const MyProfileMenu = ({ user, position, avatarUrl, onClose, onAction, onUploadAvatar }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [clampedPos, setClampedPos] = useState(null);
  const menuRef = useRef(null);

  // Close on click outside / ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    const onClick = () => onClose?.();
    window.addEventListener('keydown', onKey);
    window.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', onClick);
    };
  }, [onClose]);

  useEffect(() => {
    setPreviewUrl(avatarUrl || null);
  }, [avatarUrl]);

  useEffect(() => {
    // Reset clamped position when position changes to recalculate
    if (position) {
      const margin = 16;
      const bottomFromViewport = window.innerHeight - position.rectBottom;
      setClampedPos({ x: position.x, bottom: bottomFromViewport + margin });
    } else {
      setClampedPos(null);
    }
  }, [position]);

  useLayoutEffect(() => {
    if (!position || !menuRef.current) return;
    const { offsetWidth, offsetHeight } = menuRef.current;
    const margin = 16;

    const maxX = Math.max(margin, window.innerWidth - offsetWidth - margin);
    const clampedX = Math.min(Math.max(position.x, margin), maxX);

    // Position menu so its bottom aligns with the top of the avatar (above it)
    const bottomFromViewport = window.innerHeight - position.rectTop + margin;
    
    // Ensure menu doesn't go off the top of the screen
    const maxBottom = window.innerHeight - offsetHeight - margin;
    const clampedBottom = Math.min(bottomFromViewport, maxBottom);

    setClampedPos({ x: clampedX, bottom: clampedBottom });
  }, [position]);

  const initials = useMemo(() => {
    const name = user?.full_name || user?.username || '';
    return name.trim().slice(0, 1).toUpperCase() || '?';
  }, [user]);

  // State to track if image failed to load
  const [imageError, setImageError] = useState(false);

  // Reset image error when previewUrl changes
  useEffect(() => {
    setImageError(false);
  }, [previewUrl]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles?.length || !onUploadAvatar) return;
    const file = acceptedFiles[0];

    if (file.size > 2 * 1024 * 1024) {
      setError('File is too large (max 2MB).');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG, JPG, or WEBP images are allowed.');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const url = await onUploadAvatar(file);
      setPreviewUrl(url);
    } catch (e) {
      const msg = e?.message || 'Upload failed. Please try again.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  }, [onUploadAvatar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    maxSize: 2 * 1024 * 1024 // 2MB
  });

  if (!position || !clampedPos) return null;

  const menuStyle = {
    bottom: clampedPos.bottom,
    left: clampedPos.x
  };

  return (
    <div className="profile-menu-portal">
      <div className="profile-menu" ref={menuRef} style={menuStyle} onClick={(e) => e.stopPropagation()}>
        <div className="profile-menu__header">
          <div className="profile-menu__avatar">
            {previewUrl && !imageError ? (
              <img 
                src={previewUrl} 
                alt="avatar" 
                onError={() => setImageError(true)}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div>
            <div className="profile-menu__name">{user?.full_name || user?.username}</div>
            <div className="profile-menu__meta">{user?.role}</div>
            <div className="profile-menu__meta">{user?.email}</div>
          </div>
        </div>

        <div className="profile-menu__list">
          <button onClick={() => onAction?.('my-profile')}>My Profile</button>
          <button onClick={() => onAction?.('company-profile')}>Company Profile</button>
          <button className="profile-menu__danger" onClick={() => onAction?.('logout')}>Sign out</button>
        </div>

        <div className="profile-menu__footer">
          {/* Lo mismo que la barra inferior, y por el mismo motivo: el nombre
              sale del TOKEN que se está enviando, no del perfil cacheado. Es
              donde se habría visto en el primer minuto que la interfaz decía
              `plachi` y el servidor oía a `Ciocan`. */}
          <ActiveIdentityLabel compact />
        </div>
      </div>
    </div>
  );
};

export default MyProfileMenu;
